/**
 * EmailPreferencesPage
 * ────────────────────────────────────────────────────────────
 * Public, tokenised preference centre that the customer reaches
 * from the "Manage email preferences" link in every marketing
 * email footer. No login required — the URL carries a signed
 * email-bound HMAC token.
 *
 *   /email-preferences?email=customer@example.com&token=xxxx
 *
 * UX:
 *   • Two lists — Operational (always-on, locked) and Marketing
 *     (toggle per category).
 *   • Bottom action row: "Save preferences" + a destructive
 *     "Unsubscribe from ALL marketing" button (= legacy full
 *     mute, matches the old footer link).
 *   • Confirmation toast and a saved-state recap.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Mail, Lock, ShieldCheck, AlertTriangle, Loader2, MailX } from "lucide-react";
import { API_URL } from "../config/api";

function useQuery() {
  return useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      email: (params.get("email") || "").trim().toLowerCase(),
      token: (params.get("token") || "").trim(),
    };
  }, []);
}

export default function EmailPreferencesPage() {
  const { email, token } = useQuery();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [categories, setCategories] = useState([]);
  const [optouts, setOptouts] = useState(new Set());
  const [allMutedLegacy, setAllMutedLegacy] = useState(false);
  const [fullyBlocked, setFullyBlocked] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!email || !token) {
        setError("Missing email or token. Open this page via the link in a Europacific email.");
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(
          `${API_URL}/email-preferences/?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`
        );
        setCategories(res.data.categories || []);
        setOptouts(new Set(res.data.category_optouts || []));
        setAllMutedLegacy(!!res.data.all_marketing_blocked && !(res.data.category_optouts || []).length);
        setFullyBlocked(!!res.data.fully_blocked);
      } catch (e) {
        setError(e.response?.data?.detail || "Invalid or expired link. Please use the most recent email's link.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [email, token]);

  const toggle = (cid) => {
    setAllMutedLegacy(false);
    setOptouts((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/email-preferences/?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`,
        { category_optouts: Array.from(optouts), unsubscribe_all_marketing: false }
      );
      toast.success("Preferences saved");
      setAllMutedLegacy(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const unsubscribeAll = async () => {
    if (!window.confirm("Mute ALL marketing emails? You'll keep receiving operational shipment emails (BL, VGM, arrival notices, etc.)")) return;
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/email-preferences/?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`,
        { category_optouts: [], unsubscribe_all_marketing: true }
      );
      toast.success("All marketing muted");
      setAllMutedLegacy(true);
      setOptouts(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const operationalCats = categories.filter((c) => c.kind === "operational");
  const marketingCats = categories.filter((c) => c.kind === "marketing");
  // Internal categories are filtered server-side from this public payload,
  // but defensively skip any that slip through.
  void categories.filter((c) => c.kind === "internal");

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="prefs-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="h-5 w-5" />
              Cannot load preferences
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600">{error}</p>
            <p className="text-xs text-slate-400 mt-3">
              If you need help, reply to any Europacific email or contact{" "}
              <a href="mailto:info@europacific.com" className="text-blue-600 underline">info@europacific.com</a>.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto" data-testid="email-preferences-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-xl bg-blue-100 text-blue-700">
              <Mail className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Email preferences</h1>
              <p className="text-sm text-slate-500 font-mono">{email}</p>
            </div>
          </div>
          <p className="text-sm text-slate-600">
            Choose which Europacific emails you want to receive. Operational emails about your active shipments
            cannot be turned off — they are required for your cargo to move.
          </p>
        </div>

        {fullyBlocked && (
          <Card className="mb-4 border-rose-200 bg-rose-50">
            <CardContent className="pt-4 text-sm text-rose-700 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              This address has been hard-blocked (GDPR / legal removal). To re-subscribe, reply to any Europacific email or contact info@europacific.com.
            </CardContent>
          </Card>
        )}

        {/* Marketing — opt-out-able */}
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              Marketing &amp; informational
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Toggle each off if you don't want it. Default is everything ON.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="marketing-categories">
              {marketingCats.map((cat) => {
                const isOptedOut = optouts.has(cat.id) || allMutedLegacy;
                return (
                  <div
                    key={cat.id}
                    className="flex items-start justify-between gap-3 py-2 border-b last:border-b-0"
                    data-testid={`cat-row-${cat.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <Label className="text-sm font-medium">{cat.label}</Label>
                      <p className="text-xs text-slate-500 mt-0.5">{cat.description}</p>
                    </div>
                    <Switch
                      checked={!isOptedOut && !fullyBlocked}
                      disabled={saving || fullyBlocked}
                      onCheckedChange={() => toggle(cat.id)}
                      data-testid={`cat-toggle-${cat.id}`}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
              <Button
                variant="outline"
                onClick={unsubscribeAll}
                disabled={saving || fullyBlocked}
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="prefs-unsubscribe-all"
              >
                <MailX className="h-4 w-4 mr-2" />
                Unsubscribe from ALL marketing
              </Button>
              <Button
                onClick={save}
                disabled={saving || fullyBlocked}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="prefs-save"
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
                ) : (
                  "Save preferences"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Operational — locked */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Lock className="h-4 w-4 text-slate-500" />
              Operational (always on)
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              These emails are required for your cargo to move and cannot be muted.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2.5" data-testid="operational-categories">
              {operationalCats.map((cat) => (
                <div
                  key={cat.id}
                  className="flex items-start justify-between gap-3 py-2 border-b last:border-b-0 opacity-90"
                >
                  <div className="flex-1 min-w-0">
                    <Label className="text-sm font-medium">{cat.label}</Label>
                    <p className="text-xs text-slate-500 mt-0.5">{cat.description}</p>
                  </div>
                  <Switch checked disabled data-testid={`cat-locked-${cat.id}`} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-slate-400 text-center mt-6">
          © Europacific d.o.o. · For help, contact{" "}
          <a href="mailto:info@europacific.com" className="text-blue-600 underline">info@europacific.com</a>
        </p>
      </div>
    </div>
  );
}
